import { getAllProperties } from './utils/node-properties';
import { oneHot, getDistance } from './utils/data-preprocessing';
import Vector from './utils/vector';
import { DistanceType } from './types';
/**
 *  k-means算法 根据节点属性之间的欧氏距离将节点聚类为K个簇
 * @param data 图数据
 * @param k 质心（聚类中心）个数
 * @param seedNode 种子节点
 * @param involvedKeys 参与计算的key集合
 * @param uninvolvedKeys 不参与计算的key集合
 * @param propertyKey 属性的字段名
 * @param distanceType 距离类型
 */

var kMeans = function kMeans(data, k, involvedKeys, uninvolvedKeys, propertyKey, distanceType) {
  if (k === void 0) {
    k = 3;
  }

  if (involvedKeys === void 0) {
    involvedKeys = [];
  }

  if (uninvolvedKeys === void 0) {
    uninvolvedKeys = [];
  }

  if (propertyKey === void 0) {
    propertyKey = 'properties';
  }

  if (distanceType === void 0) {
    distanceType = DistanceType.EuclideanDistance;
  }

  var nodes = data.nodes,
      edges = data.edges; // 所有节点属性集合

  var properties = getAllProperties(nodes, propertyKey); // 所有节点属性one-hot特征向量集合s

  var allPropertiesWeight = oneHot(properties, involvedKeys, uninvolvedKeys); // 记录节点的原始index，与allPropertiesWeight对应

  for (var i = 0; i < nodes.length; i++) {
    nodes[i].originIndex = i;
  } // 初始化质心（聚类中心）


  var centroids = [];
  var centroidIndexList = [];
  var clusters = [];

  for (var i = 0; i < k; i++) {
    if (i === 0) {
      // 随机选取质心（聚类中心）
      var randomIndex = Math.floor(Math.random() * nodes.length);
      centroids[i] = allPropertiesWeight[randomIndex];
      centroidIndexList.push(randomIndex);
      clusters[i] = [nodes[randomIndex]];
      nodes[randomIndex].clusterId = String(i);
    } else {
      var maxDistance = -Infinity;
      var maxDistanceIndex = 0; // 选取与已有质心平均距离最远的点做为新的质心

      for (var m = 0; m < nodes.length; m++) {
        if (!centroidIndexList.includes(m)) {
          var totalDistance = 0;

          for (var j = 0; j < centroids.length; j++) {
            // 求节点到质心距离（默认欧式距离）
            var distance = getDistance(allPropertiesWeight[nodes[m].originIndex], centroids[j], distanceType);
            totalDistance += distance;
          } // 节点到各质心的平均距离（默认欧式距离）


          var avgDistance = totalDistance / centroids.length; // 记录到已有质心最远的的距离和节点索引

          if (avgDistance > maxDistance) {
            maxDistance = avgDistance;
            maxDistanceIndex = m;
          }
        }
      }

      centroids[i] = allPropertiesWeight[maxDistanceIndex];
      centroidIndexList.push(maxDistanceIndex);
      clusters[i] = [nodes[maxDistanceIndex]];
      nodes[maxDistanceIndex].clusterId = String(i);
    }
  }

  var iterations = 0;

  while (true) {
    for (var i = 0; i < nodes.length; i++) {
      var minDistanceIndex = 0;
      var minDistance = Infinity;

      if (!(iterations === 0 && centroidIndexList.includes(i))) {
        for (var j = 0; j < centroids.length; j++) {
          // 求节点到质心的距离（默认欧式距离）
          var distance = getDistance(allPropertiesWeight[i], centroids[j], distanceType); // 记录节点最近的质心的索引

          if (distance < minDistance) {
            minDistance = distance;
            minDistanceIndex = j;
          }
        } // 从原来的类别删除节点


        if (nodes[i].clusterId !== String(minDistanceIndex)) {
          for (var n = 0; n < clusters[minDistanceIndex].length; n++) {
            if (clusters[minDistanceIndex][n].id === nodes[i].id) {
              clusters[minDistanceIndex].splice(n, 1);
            }
          } // 将节点划分到距离最小的质心（聚类中心）所对应的类中


          clusters[minDistanceIndex].push(nodes[i]);
          nodes[i].clusterId = String(minDistanceIndex);
        }
      }
    } // 是否存在质心（聚类中心）移动


    var centroidsEqualAvg = false;

    for (var i = 0; i < clusters.length; i++) {
      var clusterNodes = clusters[i];
      var totalVector = new Vector([]);

      for (var j = 0; j < clusterNodes.length; j++) {
        totalVector = totalVector.add(new Vector(allPropertiesWeight[clusterNodes[j].originIndex]));
      } // 计算每个类别的均值向量


      var avgVector = totalVector.avg(clusterNodes.length); // 如果均值向量不等于质心向量

      if (!avgVector.equal(new Vector(centroids[i]))) {
        centroidsEqualAvg = true; // 移动/更新每个类别的质心（聚类中心）到该均值向量

        centroids[i] = avgVector.getArr();
      }
    }

    iterations++; // 如果不存在质心（聚类中心）移动或者迭代次数超过1000，则停止

    if (centroidsEqualAvg || iterations >= 1000) {
      break;
    }
  } // get the cluster edges


  var clusterEdges = [];
  var clusterEdgeMap = {};
  edges.forEach(function (edge) {
    var _a, _b;

    var source = edge.source,
        target = edge.target;
    var sourceClusterId = (_a = nodes.find(function (node) {
      return node.id === source;
    })) === null || _a === void 0 ? void 0 : _a.clusterId;
    var targetClusterId = (_b = nodes.find(function (node) {
      return node.id === target;
    })) === null || _b === void 0 ? void 0 : _b.clusterId;
    var newEdgeId = "".concat(sourceClusterId, "---").concat(targetClusterId);

    if (clusterEdgeMap[newEdgeId]) {
      clusterEdgeMap[newEdgeId].count++;
    } else {
      var newEdge = {
        source: sourceClusterId,
        target: targetClusterId,
        count: 1
      };
      clusterEdgeMap[newEdgeId] = newEdge;
      clusterEdges.push(newEdge);
    }
  });
  return {
    clusters: clusters,
    clusterEdges: clusterEdges
  };
};

export default kMeans;